import {
  BALL_RADIUS,
  type HoleLayout,
} from "@shared/physics";
import { buildDailyCourse } from "@shared/course";
import { biomeForHole } from "@shared/biome";
import { setActiveTheme } from "../theme";
import type { SceneCtx } from "../scene";
import { buildHoleVisuals, updateTrail, type HoleVisuals } from "./render";
import { HoleCamera } from "./camera";
import type { SpectatorShot } from "../api";

export interface SpectatorHandle {
  dispose(): void;
}

export interface SpectatorOptions {
  handle: string;
  countryCode: string;
  courseDate: string;
  shots: SpectatorShot[];
  /** Called when the round finishes naturally (last shot of last hole
   *  played out) so the caller can swap UI back to the lobby. */
  onComplete?: () => void;
  /** Called when the user dismisses the spectator overlay early. */
  onClose?: () => void;
  /** HUD chrome callback — fired every time the active hole changes
   *  so the caller can update an external "Hole N · stroke X" badge. */
  onHoleChange?: (info: {
    holeIndex: number;
    par: number;
    holeStrokes: number;
    totalStrokes: number;
  }) => void;
}

const BALL_Y = BALL_RADIUS + 0.06;

// Spectator-mode replay scene. Plays one player's persisted round
// from the daily `shots` table, hole by hole, shot by shot. Reuses
// the exact same render building blocks the live game uses
// (`buildHoleVisuals`, `updateTrail`, `HoleCamera`) so the visuals
// match what the player saw — same target, same planets, same trail
// colour ramp, same camera language.
//
// Architecture mirrors `src/game/demo.ts` but instead of randomly
// firing a `simulateShot` each iteration, it walks a pre-loaded list
// of persisted samples grouped by hole. Each hole loads its real
// daily layout via `buildDailyCourse(courseDate)` and the camera
// frames it with the same intro sweep the player got. Once a hole's
// shots are exhausted we advance to the next; once the round runs
// out we trigger `onComplete`.
export function startSpectator(
  scene: SceneCtx,
  opts: SpectatorOptions,
): SpectatorHandle {
  let alive = true;
  let visuals: HoleVisuals | null = null;
  let camera: HoleCamera | null = null;
  let hole: HoleLayout | null = null;

  // Group shots by hole index so we can play through each hole's
  // shots in order, then advance to the next non-empty hole.
  const shotsByHole = new Map<number, SpectatorShot[]>();
  for (const s of opts.shots) {
    let bucket = shotsByHole.get(s.holeIndex);
    if (!bucket) {
      bucket = [];
      shotsByHole.set(s.holeIndex, bucket);
    }
    bucket.push(s);
  }
  // Sorted hole indices that actually have shots — usually 0..8 but
  // a partial round (player quit mid-way) might skip late holes.
  const holeOrder = Array.from(shotsByHole.keys()).sort((a, b) => a - b);

  // Daily course rebuilt deterministically from the courseDate — same
  // seed the player saw, same layout. `buildDailyCourse` is pure so
  // calling it again is fine.
  const course = buildDailyCourse(opts.courseDate);

  let holePtr = 0; // index into holeOrder
  let shotPtr = 0; // index into the current hole's shots[]
  let totalStrokes = 0;
  let holeStrokes = 0;

  // Active shot replay window. Same shape as demo.ts so the per-frame
  // interpolation logic is identical.
  let active: {
    samples: { t: number; x: number; z: number }[];
    startedAt: number;
    durationSec: number;
    headIdx: number;
  } | null = null;
  // Wall-clock at which the next shot in the current hole should
  // start. Slight pause between shots so the user can see the rest
  // position before the next slingshot.
  let nextShotAt = 0;
  // Same gap between holes so the camera intro has time to play.
  const INTER_SHOT_PAUSE = 0.6;
  const INTER_HOLE_PAUSE = 1.2;

  function disposeVisuals(): void {
    if (visuals) {
      scene.scene.remove(visuals.root);
      visuals.dispose();
      visuals = null;
    }
  }

  function loadHole(holeIndex: number): void {
    disposeVisuals();
    hole = course.holes[holeIndex] ?? null;
    if (!hole) return;
    setActiveTheme(biomeForHole(opts.courseDate, holeIndex));
    scene.backdrop.applyTheme();
    visuals = buildHoleVisuals(hole);
    scene.scene.add(visuals.root);
    if (camera) {
      camera.setHole(hole, { intro: true });
    } else {
      camera = new HoleCamera(scene.camera, hole);
      camera.setHole(hole, { intro: true });
    }
    if (visuals) {
      visuals.ball.position.set(hole.tee.x, BALL_Y, hole.tee.z);
      updateTrail(visuals, [], 0);
    }
    holeStrokes = 0;
    opts.onHoleChange?.({
      holeIndex,
      par: hole.par,
      holeStrokes: 0,
      totalStrokes,
    });
  }

  function startNextShot(t: number): void {
    if (!hole || !visuals || !camera) return;
    const currentHole = holeOrder[holePtr];
    if (currentHole === undefined) return;
    const bucket = shotsByHole.get(currentHole)!;
    const shot = bucket[shotPtr];
    if (!shot) return;

    const samples = shot.samples;
    if (samples.length < 2) {
      // Defensive: a malformed shot row with too few samples — skip.
      shotPtr++;
      return;
    }

    // Cinematic shot-cam, same as live play.
    camera.beginShotCam(
      { x: samples[0]!.x, z: samples[0]!.z },
      { x: shot.aim.aimX, z: shot.aim.aimZ },
    );

    const dur = samples[samples.length - 1]!.t;
    active = {
      samples,
      startedAt: t,
      durationSec: dur,
      headIdx: 0,
    };
    holeStrokes = shot.strokes;
    totalStrokes += 1;
    opts.onHoleChange?.({
      holeIndex: currentHole,
      par: hole.par,
      holeStrokes,
      totalStrokes,
    });
  }

  function advanceAfterShot(t: number): void {
    const currentHole = holeOrder[holePtr];
    if (currentHole === undefined) return;
    const bucket = shotsByHole.get(currentHole)!;
    shotPtr++;
    if (shotPtr < bucket.length) {
      // Still more shots on this hole.
      nextShotAt = t + INTER_SHOT_PAUSE;
      return;
    }
    // Hole done — advance.
    holePtr++;
    shotPtr = 0;
    if (holePtr >= holeOrder.length) {
      // Round complete. Hold on the final hole for a beat, then signal
      // the caller. We don't tear ourselves down — let the caller
      // dispose so it can sync UI transitions.
      window.setTimeout(() => {
        if (alive) opts.onComplete?.();
      }, 1500);
      return;
    }
    const nextHole = holeOrder[holePtr]!;
    nextShotAt = t + INTER_HOLE_PAUSE;
    loadHole(nextHole);
  }

  // Kick off with the first hole.
  if (holeOrder.length > 0) {
    loadHole(holeOrder[0]!);
    nextShotAt = scene.clock.getElapsedTime() + 0.9;
  } else {
    // No shots → bail immediately so the caller can show an empty state.
    window.setTimeout(() => {
      if (alive) opts.onComplete?.();
    }, 50);
  }

  const off = scene.onFrame((t, _dt) => {
    if (!alive || !visuals || !hole || !camera) return;

    let bx: number;
    let bz: number;

    if (active) {
      const elapsed = t - active.startedAt;
      if (elapsed >= active.durationSec) {
        // Shot finished — park at end-state and trail the full path,
        // then schedule the next one.
        const last = active.samples[active.samples.length - 1]!;
        bx = last.x;
        bz = last.z;
        const trailPoints = active.samples.map((s) => ({
          x: s.x,
          y: BALL_Y,
          z: s.z,
        }));
        updateTrail(visuals, trailPoints, trailPoints.length);
        active = null;
        camera.endShotCam();
        advanceAfterShot(t);
      } else {
        // Bracket-lookup interpolation — identical to demo.ts.
        const samples = active.samples;
        let lo = 0;
        let hi = samples.length - 1;
        while (lo < hi - 1) {
          const mid = (lo + hi) >> 1;
          if (samples[mid]!.t <= elapsed) lo = mid;
          else hi = mid;
        }
        const a = samples[lo]!;
        const b = samples[hi]!;
        const span = Math.max(0.0001, b.t - a.t);
        const k = (elapsed - a.t) / span;
        bx = a.x + (b.x - a.x) * k;
        bz = a.z + (b.z - a.z) * k;
        // Walk trail head forward in step with elapsed sim time.
        while (
          active.headIdx < samples.length - 1 &&
          samples[active.headIdx + 1]!.t <= elapsed
        ) {
          active.headIdx++;
        }
        const trailPoints: { x: number; y: number; z: number }[] = [];
        for (let i = 0; i <= active.headIdx; i++) {
          const s = samples[i]!;
          trailPoints.push({ x: s.x, y: BALL_Y, z: s.z });
        }
        trailPoints.push({ x: bx, y: BALL_Y, z: bz });
        updateTrail(visuals, trailPoints, trailPoints.length);
        camera.updateShotCamBall(bx, bz);
      }
    } else if (t >= nextShotAt && holePtr < holeOrder.length) {
      startNextShot(t);
      // `startNextShot` mutates the closure-level `active`. TS narrowed
      // to `null` from the outer-`if` branch above, so we need to
      // ignore that narrowing and re-fetch.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const justStarted = active as any;
      if (justStarted) {
        bx = justStarted.samples[0].x as number;
        bz = justStarted.samples[0].z as number;
      } else {
        bx = hole.tee.x;
        bz = hole.tee.z;
      }
    } else {
      // Idle between shots — keep the ball at its last position so the
      // camera can chase it cleanly.
      const lastVis = visuals.ball.position;
      bx = lastVis.x;
      bz = lastVis.z;
    }

    visuals.ball.position.set(bx, BALL_Y, bz);

    // Lightweight per-frame ticks. Identical to demo.ts — we don't
    // tick gates / comets / wormholes / wind because the spectator
    // courses are real daily layouts that may have these visuals;
    // for now they render statically. Adding per-frame ticks for
    // them is a follow-up if playback feels off on H7+.
    for (const p of visuals.planets) {
      p.rotation.y += (p.userData.spin ?? 0.02) * 0.02;
      const u = (p.material as { uniforms?: { uTime?: { value: number } } })
        .uniforms;
      if (u?.uTime) u.uTime.value = t;
    }
    const haloU = (visuals.targetHalo.material as {
      uniforms?: { uTime?: { value: number } };
    }).uniforms;
    if (haloU?.uTime) haloU.uTime.value = t;
    if (visuals.targetDiskMat.uniforms.uTime) {
      visuals.targetDiskMat.uniforms.uTime.value = t;
    }
    for (const m of visuals.targetExtraMats) {
      if (m.uniforms.uTime) m.uniforms.uTime.value = t;
    }
    const partU = (visuals.targetParticles.material as {
      uniforms?: { uTime?: { value: number } };
    }).uniforms;
    if (partU?.uTime) partU.uTime.value = t;
    visuals.targetLensRing.lookAt(scene.camera.position);
    for (const w of visuals.gravityWells) {
      const u = (w.material as { uniforms?: { uTime?: { value: number } } })
        .uniforms;
      if (u?.uTime) u.uTime.value = t;
    }
    if (visuals.boundaryMat.uniforms.uTime) {
      visuals.boundaryMat.uniforms.uTime.value = t;
    }
    visuals.boundaryParticles.update(t);

    camera.followBall(bx, BALL_Y, bz, 0.06, t);
  });

  return {
    dispose() {
      if (!alive) return;
      alive = false;
      off();
      disposeVisuals();
      opts.onClose?.();
    },
  };
}
